/** a tiny "any form mid-edit?" registry. forms (RFI, invoice, etc.)
 *  register themselves as dirty when their state diverges from initial,
 *  and unregister when clean or unmounted. consumers (the company
 *  switcher) check before destructive navigation so users get a chance
 *  to back out instead of silently re-scoping their work.
 *
 *  design choices:
 *
 *  - hasDirty() is a method, not a state value. forms register through
 *    a ref so updates don't re-render every consumer on every keystroke.
 *    the switcher only reads at click-time, which is a discrete moment.
 *
 *  - keys are caller-chosen strings. the registry doesn't care about
 *    semantics: RFI uses 'rfi-form', invoice uses 'invoice-form', etc.
 *    this lets a future API surface a list of *which* forms are dirty
 *    if we want richer confirm-dialog copy.
 *
 *  - the provider is optional. consumers that skip it get a no-op
 *    registry (hasDirty always false) so tests + isolated stories don't
 *    need to wrap. */

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react'

export type DirtyFormRegistry = {
  register: (id: string) => void
  unregister: (id: string) => void
  hasDirty: () => boolean
  listDirty: () => string[]
}

const NOOP_REGISTRY: DirtyFormRegistry = {
  register: () => undefined,
  unregister: () => undefined,
  hasDirty: () => false,
  listDirty: () => []
}

const DirtyFormContext = createContext<DirtyFormRegistry>(NOOP_REGISTRY)

export function DirtyFormProvider({ children }: { children: ReactNode }) {
  const dirtyRef = useRef<Set<string>>(new Set())

  const value = useMemo<DirtyFormRegistry>(
    () => ({
      register: (id: string) => {
        dirtyRef.current.add(id)
      },
      unregister: (id: string) => {
        dirtyRef.current.delete(id)
      },
      hasDirty: () => dirtyRef.current.size > 0,
      listDirty: () => [...dirtyRef.current]
    }),
    []
  )

  return (
    <DirtyFormContext.Provider value={value}>
      {children}
    </DirtyFormContext.Provider>
  )
}

/** call from any form context. id should be stable per form-instance.
 *  unregisters on unmount so a navigated-away form doesn't keep the
 *  switcher gated. */
export function useRegisterDirtyForm(id: string, isDirty: boolean): void {
  const registry = useContext(DirtyFormContext)
  useEffect(() => {
    if (isDirty) registry.register(id)
    else registry.unregister(id)
    return () => registry.unregister(id)
  }, [registry, id, isDirty])
}

/** call from the switcher (or any destructive-navigation surface). the
 *  returned registry has hasDirty() / listDirty(), call them at the
 *  moment of the action, not in render, to avoid stale closures. */
export function useDirtyForms(): DirtyFormRegistry {
  return useContext(DirtyFormContext)
}

/** convenience for the common shape: reducer-based forms whose initial
 *  state is computed from server data. snapshot the initial in a ref on
 *  first render, then compare each subsequent state against it,
 *  ignoring transient fields (submitError, submitting, *Touched flags,
 *  etc.) the caller doesn't want to count as user edits. shallow
 *  comparison: arrays + plain objects in nested fields will always
 *  read as dirty after any reducer update, for those shapes write a
 *  custom isDirty and call useRegisterDirtyForm directly.
 */
export function useDirtyVsInitial<S extends Record<string, unknown>>(
  id: string,
  state: S,
  ignoreFields: readonly (keyof S)[] = []
): void {
  /** initial snapshot captured on first render, subsequent renders
   *  compare against it. we intentionally do NOT update the ref on
   *  remount in the same component instance: the consumer's
   *  initialState() function captured the server data, and "dirty"
   *  means "diverged from THAT initial." server refetch that returns
   *  new data will remount the provider via key change, which is the
   *  existing pattern in supplier-onboarding/etc.
   */
  const initialRef = useRef<S | null>(null)
  if (initialRef.current === null) {
    initialRef.current = state
  }
  let dirty = false
  for (const key of Object.keys(state) as (keyof S)[]) {
    if (ignoreFields.includes(key)) continue
    if (state[key] !== initialRef.current[key]) {
      dirty = true
      break
    }
  }
  useRegisterDirtyForm(id, dirty)
}

/** exported only for the test harness. handy when a test wants to
 *  verify the no-provider branch returns the noop registry. */
export {
  DirtyFormContext as _DirtyFormContext,
  NOOP_REGISTRY as _NOOP_REGISTRY
}
